import { css } from '@emotion/react';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { RadioGroup } from '@anyui/core';
import { Button, Form, Icon, Radio, TextCutter } from '@anytag/shared-atoms';
import { TextField, TextareaField, CheckBoxesField, UploadCSVCouponModal } from '@anytag/shared-molecules';
import { CampaignTrackingOption, EngagementCampaignStatus } from '@anytag/graphql';
import { THEME } from '@anytag/shared-constants';
import { useToggleState } from '@anytag/shared-hooks';
import { DisabledFields } from '@anytag/shared-types';
import { EngagementCampaignFormValues, EngagementCampaignFormKeys } from '../types';

enum TrackingReferralCodeOption {
  MANUAL = 'MANUAL',
  UPLOAD_FILE = 'UPLOAD_FILE'
}

interface TrackingProps {
  disabledFields: DisabledFields<EngagementCampaignFormKeys>;
}

export const Tracking = ({ disabledFields }: TrackingProps) => {
  const { t } = useTranslation();
  const { status: isOpening, open: openPopover, close: closePopover } = useToggleState();
  const {
    reset,
    setValue,
    getValues,
    formState: { defaultValues }
  } = useFormContext<EngagementCampaignFormValues>();
  const values = getValues();
  const { trackingOptions, oldCouponList, couponUploadFiles } = values;
  const [trackingReferralCodeOption, setTrackingReferralCodeOption] = useState(TrackingReferralCodeOption.MANUAL);

  const status = defaultValues?.status;
  const isOngoing = status === EngagementCampaignStatus.ONGOING;
  const isFinished = status === EngagementCampaignStatus.FINISHED;
  const hasTrackingTune = !!trackingOptions?.includes(CampaignTrackingOption.TUNE);
  const hasTrackingReferral = !!trackingOptions?.includes(CampaignTrackingOption.REFERRAL_CODE);
  const couponFileNames = (() => {
    const fileNames: string[] = [];

    couponUploadFiles.forEach((file) => {
      const decodeUrl = decodeURI(file);
      fileNames.push(decodeUrl.substring(decodeUrl.lastIndexOf('/') + 1));
    });

    return fileNames;
  })();

  const handleFileUpload = (fileUrl: string) => {
    if (fileUrl) {
      setValue('couponList', '');
      setValue('couponUploadFiles', [...couponUploadFiles, fileUrl]);

      closePopover();
    }
  };

  const handleTrackingTuneChecked = () => {
    // Currently, we only support 1 of Tune or Referral tracking as the same time
    const { trackingOptions, ...restValues } = values;
    reset({
      ...restValues,
      couponList: '',
      couponUploadFiles: [],
      trackingOptions: trackingOptions?.includes(CampaignTrackingOption.TUNE) ? [] : [CampaignTrackingOption.TUNE]
    });
  };

  const handleTrackingReferralChecked = () => {
    // Currently, we only support 1 of Tune or Referral tracking as the same time
    const { trackingOptions, ...restValues } = values;
    reset({
      ...restValues,
      landingPageUrl: '',
      trackingOptions: trackingOptions?.includes(CampaignTrackingOption.REFERRAL_CODE)
        ? []
        : [CampaignTrackingOption.REFERRAL_CODE]
    });
  };

  return (
    <>
      <Form.SectionTitle index={3} label={t('General.Tracking')} />

      <CheckBoxesField<EngagementCampaignFormKeys>
        name="trackingOptions"
        onChange={handleTrackingTuneChecked}
        disabled={disabledFields?.trackingOptions}
        options={[{ label: t<string>('Track clicks/actions on the website'), value: CampaignTrackingOption.TUNE }]}
      />

      {hasTrackingTune && (
        <TextField<EngagementCampaignFormKeys>
          name="landingPageUrl"
          placeholder="https://anytag.tech"
          title={t('TextForm.Landing URL')}
          help={t('Tooltip.Landing URL Help')}
          disabled={disabledFields?.landingPageUrl}
          required
        />
      )}

      <CheckBoxesField<EngagementCampaignFormKeys>
        name="trackingOptions"
        onChange={handleTrackingReferralChecked}
        disabled={disabledFields?.trackingOptions}
        options={[
          {
            label: t<string>('Track conversions/sales by referral code'),
            value: CampaignTrackingOption.REFERRAL_CODE
          }
        ]}
      />

      {hasTrackingReferral ? (
        <Form.Field className="pl-6">
          <RadioGroup
            value={trackingReferralCodeOption}
            onChange={(value) => setTrackingReferralCodeOption(value as TrackingReferralCodeOption)}
          >
            <Radio
              label={t('Manual')}
              disabled={disabledFields?.couponList}
              value={TrackingReferralCodeOption.MANUAL}
            />
            <Radio
              label={t('Upload File')}
              disabled={disabledFields?.couponUploadFiles}
              value={TrackingReferralCodeOption.UPLOAD_FILE}
            />
          </RadioGroup>

          {trackingReferralCodeOption === TrackingReferralCodeOption.MANUAL ? (
            <>
              <p css={{ fontSize: '14px', lineHeight: '16px', marginTop: '16px' }}>{t('trackConversionNote2')}</p>

              {(isOngoing || isFinished) && oldCouponList ? (
                <p css={{ fontSize: '13px', marginTop: '16px', wordBreak: 'break-word' }}>
                  {oldCouponList.replace(/,/g, ', ')}
                </p>
              ) : null}

              {isOngoing ? (
                <TextareaField<EngagementCampaignFormKeys>
                  name="newCouponList"
                  disabled={isFinished}
                  onChange={(field, value) => {
                    setValue(field, value);
                    setValue('couponUploadFiles', []);
                    setValue('couponList', [oldCouponList, value].join(', '));
                  }}
                  placeholder="code0001, code0002, code0003, code0004"
                />
              ) : (
                <TextareaField<EngagementCampaignFormKeys>
                  name="couponList"
                  disabled={isFinished}
                  onChange={(field, value) => {
                    setValue(field, value);
                    setValue('couponUploadFiles', []);
                  }}
                  placeholder="code0001, code0002, code0003, code0004"
                />
              )}
            </>
          ) : (
            <>
              {couponFileNames.length > 0
                ? couponFileNames.map((fileName, index) => (
                    <div key={index} css={styles.uploadFile}>
                      <Icon icon="file" variant="gray" className="mr-1.5" size="14px" />
                      <TextCutter css={{ fontSize: '14px' }} text={fileName} />
                      {!disabledFields?.couponUploadFiles ? (
                        <Icon
                          size="10px"
                          icon="close"
                          variant="gray"
                          css={{ marginLeft: 'auto', cursor: 'pointer' }}
                          onClick={() => {
                            const newCouponUploadFilesValue = [...couponUploadFiles];
                            newCouponUploadFilesValue.splice(index, 1);
                            setValue('couponUploadFiles', newCouponUploadFilesValue);
                          }}
                        />
                      ) : null}
                    </div>
                  ))
                : null}

              <Button
                variant="outline"
                onClick={openPopover}
                disabled={isFinished}
                title={t('Upload CSV')}
                className="mt-2 px-6"
              />
            </>
          )}
        </Form.Field>
      ) : null}

      <UploadCSVCouponModal isOpen={isOpening} onClose={closePopover} onUploadFile={handleFileUpload} />
    </>
  );
};

const styles = {
  uploadFile: css({
    display: 'flex',
    padding: '16px',
    marginTop: '8px',
    borderRadius: '5px',
    alignItems: 'center',
    border: THEME.border.base,
    backgroundColor: THEME.background.colors.gray.lv1
  })
};
